import React, { useState, useEffect } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cross, ArrowLeft, LogIn } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate("/admin", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/church-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-deepblue-900">Chargement…</div>;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Connexion réussie");
      navigate("/admin");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-deepblue-900" data-testid="admin-login-page">
      <img
        src="https://images.pexels.com/photos/37423340/pexels-photo-37423340.jpeg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-deepblue-900 via-deepblue-900/95 to-deepblue-800" />
      <div className="absolute inset-0 grain opacity-60" />

      <div className="relative w-full max-w-md mx-4">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-gold mb-6 text-sm" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </Link>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            {info?.logo_url ? (
              <img src={info.logo_url} alt={info?.name} className="w-24 h-24 mx-auto rounded-full object-cover ring-4 ring-gold/40 bg-white mb-4 shadow-2xl" />
            ) : (
              <div className="w-16 h-16 mx-auto rounded-full bg-gold/20 flex items-center justify-center border border-gold/40 mb-4">
                <Cross className="w-7 h-7 text-gold" strokeWidth={2.4} />
              </div>
            )}
            <h1 className="font-serif text-3xl text-white">{info?.name || "Espace administrateur"}</h1>
            <p className="mt-2 text-white/60 text-sm">Connectez-vous pour gérer votre église</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label htmlFor="email" className="text-white/80 text-xs uppercase tracking-widest">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-white/95 border-transparent text-deepblue-900"
                placeholder="admin@canaanmeigda.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80 text-xs uppercase tracking-widest">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-white/95 border-transparent text-deepblue-900"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="rounded-lg bg-red-500/20 border border-red-500/40 text-red-100 text-sm px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={submitting} className="w-full bg-gold hover:bg-gold-hover text-white rounded-full py-6" data-testid="login-submit">
              <LogIn className="w-4 h-4 mr-2" />
              {submitting ? "Connexion…" : "Se connecter"}
            </Button>

            <div className="text-center pt-2">
              <Link to="/admin/forgot-password" data-testid="forgot-password-link"
                className="text-sm text-white/70 hover:text-gold underline underline-offset-4">
                Mot de passe oublié ?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
